/*
 * AsrAdapter — one interface over the two ASR engines the censor can drive.
 *
 *   ru            → gigaam-asr-worker.js  (GigaAM-v3 e2e-CTC, self-hosted 225 MB)
 *   zh / yue      → sensevoice-asr-worker.js (SenseVoice-Small CTC, 237 MB) — OPT-IN: only
 *                   when the caller passes `cfg.urls.sensevoice`. Measured on SONGS
 *                   (2026-09-18: Mandarin CER 49.2 % whisper-small → 22.7 %; Cantonese, for which
 *                   whisper-small has no language token, 45.8 % → 15.0 %). Speech tools have not measured it
 *                   yet, so they keep whisper until they do — hence opt-in, not a default.
 *   everything else → whisper-worker.js   (transformers.js, HF-hosted, word timestamps)
 *
 * Both produce the same shape the detector + region builder already expect:
 *     { words: [{ text, start, end, conf }], detectedLang }
 *
 * Why an adapter and not a second code path in the tool: the tool's ASR
 * consumer (progress bar, watchdog, transcript, region builder) is engine-
 * agnostic — only the worker protocol differs. Keeping the difference HERE
 * means the censoring logic cannot drift per language.
 *
 * Protocol differences absorbed:
 *   • gigaam: init{modelUrl,melUrl,vocabUrl} → transcribe{pcm f32, jobId}
 *             → progress{doneS,totalS,rtf} / words_partial{words} / result{words}
 *   • sensevoice: the same protocol by construction (sibling worker); differs only in
 *             its init payload {modelUrl,tokensUrl,cmvnUrl} and a `lang` on transcribe.
 *   • whisper: (no init) → transcribe{audioBuffer, modelSize, language,
 *             wordTimestamps} → status/backend/partial → result{text, chunks}
 *     Whisper reports no per-window progress in the same shape, so we emit a
 *     synthetic one from its `partial` stream (chunk timestamps are real).
 */
(function (root) {
    'use strict';

    var SR = 16000;

    /* A job the page has moved on from is a CANCEL, and says so by name — the
       message «aborted» alone was also what a dying runtime prints. */
    function abortedError() { var e = new Error('aborted'); e.name = 'AbortError'; return e; }

    /* Whisper's model tiers by device budget. `tiny` mangles profanity
       (measured: it euphemises and drops word boundaries); `base` is the
       floor that transcribes swearing verbatim. `small` is 3x slower on
       WASM — only for desktops with real memory. */
    function pickWhisperModel() {
        var mem = navigator.deviceMemory || 0;
        var mob = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
        if (mob || (mem && mem <= 4)) return 'base';
        return 'base';   // measured: `small` gains ~nothing on profanity recall, costs 3x
    }

    /* ── CTC workers: GigaAM (Russian), SenseVoice (Chinese) ────────────────
       One adapter for both: the workers speak the same protocol. `spec` carries the only
       differences — which URLs go into `init`, and what `transcribe` adds. */
    var CTC_GIGAAM = {
        engine: 'gigaam', lang: 'ru',
        urls: function (cfg) { return cfg.urls; },
        initMsg: function (u) { return { modelUrl: u.model, melUrl: u.mel, vocabUrl: u.vocab }; },
        jobMsg: function () { return {}; }
    };
    function ctcSenseVoice(lang) {
        return {
            engine: 'sensevoice', lang: lang,
            urls: function (cfg) { return cfg.urls.sensevoice; },
            initMsg: function (u) { return { modelUrl: u.model, tokensUrl: u.tokens, cmvnUrl: u.cmvn }; },
            jobMsg: function () { return { lang: lang }; }
        };
    }
    function createGigaam(cfg, hooks) { return createCtc(CTC_GIGAAM, cfg, hooks); }

    function createCtc(spec, cfg, hooks) {
        var worker = null, ready = false, initPromise = null, goAsked = false;
        var urls = spec.urls(cfg);

        /* Which ORT build the worker loads is the owner's answer (window.__ortBuild),
           carried on the worker's URL — see gigaam-asr-worker.js. `simdOnly`: these
           models were never measured on a CPU without SIMD, that CPU keeps its
           present answer. */
        function workerUrl() {
            var B = root.__ortBuild;
            return (B && B.workerUrl) ? B.workerUrl(urls.worker, { simdOnly: true }) : urls.worker;
        }

        function boot() {
            return new Promise(function (resolve, reject) {
                var url = workerUrl();
                var fail = function (e) { e.ortLegacy = /[?&]ortb=/.test(url); reject(e); };
                try { worker = new Worker(url); }
                catch (e) { fail(e); return; }
                worker.onerror = function (ev) { if (!ready) fail(new Error((ev && ev.message) || 'worker error')); };
                worker.onmessage = function (ev) {
                    var m = ev.data || {};
                    if (m.type === 'init_progress') hooks.onModelProgress(m.loadedMB, m.totalMB);
                    else if (m.type === 'init_fetched') { if (hooks.onModelFetched) hooks.onModelFetched(); }
                    else if (m.type === 'init_preparing') hooks.onModelPreparing();
                    else if (m.type === 'init_done') { ready = true; hooks.onModelReady(m.info && m.info.initMs, m.info || null); resolve(); }
                    else if (m.type === 'error' && !ready) { var e = new Error(m.message); e.pcCode = m.code; fail(e); }
                };
                // deferSession: скачивание — сразу (сеть параллелится с
                // извлечением), а создание ORT-сессии (второй горб памяти)
                // ждёт go() со страницы — см. gigaam-asr-worker.js.
                worker.postMessage(Object.assign({ type: 'init', deferSession: true }, spec.initMsg(urls)));
                if (goAsked) worker.postMessage({ type: 'init_go' });   // go() успел раньше ensure()
            });
        }

        function dropWorker() {
            ready = false;
            try { if (worker) worker.terminate(); } catch (e) { /* noop */ }
            worker = null;
        }

        function ensure() {
            if (initPromise) return initPromise;
            initPromise = boot().catch(function (e) {
                /* «no available backend found … RangeError: Out of memory» is not this
                   device running out of memory: the browser could not RESERVE the shared
                   4 GiB the current ORT build imports (WebKit before 18). The owner marks
                   the browser and answers ORT 1.18, which needs no reservation — once
                   more in a fresh worker (the model is already in the cache), after a
                   pause: an immediate retry meets the same address space. */
                var B = root.__ortBuild;
                if (!(B && B.initFailed && B.initFailed(e, { legacy: e && e.ortLegacy ? 'legacy' : '', from: spec.engine }))) throw e;
                dropWorker();
                return new Promise(function (r) { setTimeout(r, 1000); }).then(boot);
            });
            initPromise.catch(function () { initPromise = null; dropWorker(); });
            return initPromise;
        }

        function transcribe(pcmF32, job, opts) {
            return new Promise(function (resolve, reject) {
                var w = worker;
                var handler = function (ev) {
                    var m = ev.data || {};
                    if (m.jobId !== undefined && m.jobId !== job) return;
                    if (hooks.isStale(job)) { cleanup(); reject(abortedError()); return; }
                    if (m.type === 'progress') hooks.onProgress(m.doneS, m.totalS, m.rtf);
                    else if (m.type === 'words_partial') hooks.onPartialWords(m.words);
                    else if (m.type === 'result') { cleanup(); resolve({ words: m.words, text: m.text || '', rtf: m.stats && m.stats.rtf, stats: m.stats, detectedLang: spec.lang, prompt: m.prompt || null }); }
                    else if (m.type === 'error') { cleanup(); var e = new Error(m.message); e.pcCode = m.code; reject(e); }
                    else if (m.type === 'aborted') { cleanup(); reject(abortedError()); }
                };
                function cleanup() { w.removeEventListener('message', handler); }
                w.addEventListener('message', handler);
                var buf = pcmF32.buffer;
                w.postMessage(Object.assign({ type: 'transcribe', pcm: pcmF32, jobId: job, __debugHang: !!(opts && opts.debugHang) }, spec.jobMsg()), [buf]);
            });
        }

        return {
            engine: spec.engine,
            lang: spec.lang,
            // Both CTC engines run ONNX on WASM/CPU — a constant key keeps the
            // persisted speed measurement shape uniform across engines.
            backend: function () { return 'wasm'; },
            init: ensure,
            // «Извлечение закончено — можно разворачивать сессию». Идемпотентно;
            // до ensure() тоже безопасно: флаг дошлётся сразу после init.
            go: function () { goAsked = true; try { if (worker) worker.postMessage({ type: 'init_go' }); } catch (e) { /* noop */ } },
            transcribe: transcribe,
            // The CTC workers honour a cooperative abort — they check between
            // windows, so the model stays loaded for the next file.
            abort: function () { try { if (worker) worker.postMessage({ type: 'abort' }); } catch (e) { /* noop */ } },
            isDead: function () { return false; },
            terminate: function () { try { if (worker) worker.terminate(); } catch (e) { /* noop */ } worker = null; initPromise = null; ready = false; }
        };
    }

    /* ── Language identification from the transcript ─────────────────────
       Whisper knows the language internally, but transformers.js's ASR pipeline
       does not expose the decoder's language token, so an 'auto' run comes back
       with `language: undefined`. Rather than shrug, identify it from the text:
       script first (Cyrillic/Hangul/Arabic are unambiguous for our nine), then
       function-word voting for the Latin six. Function words are the highest-
       signal, lowest-cost cue — they are the most frequent tokens in any
       sentence and barely overlap across these languages. */
    /* Function words carry the most signal per token. Words shared across these
       languages ("de", "la", "en", "a") are deliberately absent — they vote for
       everyone and decide nothing; the discriminative ones stay. */
    var LANG_MARKERS = {
        en: ['the', 'and', 'of', 'to', 'is', 'was', 'this', 'that', 'with', 'you', 'what', 'my', 'for', 'it',
             'at', 'on', 'in', 'he', 'she', 'they', 'have', 'has', 'are', 'were', 'from', 'about', 'been', 'will'],
        es: ['el', 'los', 'las', 'una', 'por', 'con', 'este', 'esta', 'muy', 'como', 'pero', 'todo', 'todos',
             'nos', 'son', 'para', 'del', 'al', 'ese', 'esa', 'hay', 'tiene', 'porque', 'cuando', 'donde'],
        pt: ['os', 'as', 'em', 'uma', 'nao', 'não', 'com', 'esse', 'essa', 'meu', 'voce', 'você', 'isso', 'vai',
             'ao', 'na', 'no', 'dos', 'das', 'pra', 'muito', 'mais', 'sao', 'são', 'foi', 'seu', 'sua', 'ele', 'ela'],
        de: ['der', 'die', 'das', 'und', 'ist', 'nicht', 'ich', 'du', 'ein', 'eine', 'einen', 'einem', 'einer',
             'mit', 'auf', 'zum', 'zur', 'schon', 'den', 'dem', 'des', 'für', 'fur', 'sie', 'wir', 'sind', 'war',
             'am', 'im', 'wieder', 'nach', 'noch', 'auch', 'aber', 'wenn', 'werden', 'haben'],
        fr: ['le', 'les', 'et', 'est', 'une', 'des', 'je', 'ce', 'pas', 'dans', 'mes', 'ma', 'mon', 'sont',
             'qui', 'quel', 'quelle', 'son', 'sa', 'ses', 'nos', 'notre', 'vous', 'nous', 'ils', 'elle', 'avec',
             'sur', 'plus', 'tout', 'cette', 'mais', 'être', 'etre', 'avoir', 'aux'],
        id: ['yang', 'dan', 'di', 'ini', 'itu', 'saya', 'aku', 'tidak', 'dengan', 'ke', 'dari', 'sudah', 'lagi',
             'sangat', 'tadi', 'setiap', 'hari', 'sekali', 'kenapa', 'terus', 'malam', 'akan', 'bisa', 'juga',
             'untuk', 'pada', 'adalah', 'kami', 'kita', 'mereka', 'dia']
    };

    /* Orthographic evidence: characters/sequences that only one of the six uses. */
    var LANG_ORTHO = [
        { lg: 'es', re: /[¿¡]|ñ/, w: 3 },
        { lg: 'pt', re: /[ãõ]|ç[aou]|ção/, w: 3 },
        { lg: 'de', re: /ß|[äöü]|sch[aeiou]/, w: 2 },
        { lg: 'fr', re: /[œŒ]|(^|\s)(l|d|j|m|n|s|t|c)['’]/, w: 2 },
        { lg: 'id', re: /(^|\s)(ke|di)\s|nya(\s|$)|kan(\s|$)/, w: 1 }
    ];

    function guessLangFromText(text) {
        var s = String(text || '');
        if (!s.trim()) return null;
        // Unambiguous scripts decide immediately.
        if (/[Ѐ-ӿ]/.test(s)) return 'ru';
        if (/[가-힯ᄀ-ᇿ]/.test(s)) return 'ko';
        if (/[؀-ۿ]/.test(s)) return 'ar';

        var lower = s.toLowerCase();
        var toks = lower.replace(/[^\p{L}\s]/gu, ' ').split(/\s+/).filter(Boolean);
        if (!toks.length) return null;

        var scores = {};
        Object.keys(LANG_MARKERS).forEach(function (lg) {
            var set = LANG_MARKERS[lg], n = 0;
            for (var i = 0; i < toks.length; i++) if (set.indexOf(toks[i]) >= 0) n++;
            scores[lg] = n;
        });
        LANG_ORTHO.forEach(function (o) { if (o.re.test(lower)) scores[o.lg] = (scores[o.lg] || 0) + o.w; });

        var ranked = Object.keys(scores).sort(function (a, b) { return scores[b] - scores[a]; });
        var top = ranked[0], second = ranked[1];
        // Decide only on a clear margin: one shared word is not evidence, and a
        // tie between two languages must stay undecided (the UI then says so and
        // the user picks the language explicitly).
        if (!scores[top]) return null;
        if (scores[top] >= 2 && scores[top] >= scores[second] * 1.5) return top;
        if (scores[top] === 1 && !scores[second]) return top;
        return null;
    }

    /* ── Whisper (everything else) ──────────────────────────────────────── */
    function createWhisper(cfg, hooks, lang) {
        var worker = null, initPromise = null;
        /* `cfg.whisperModel` lets a caller override the censor's default. It
           exists because the two consumers have opposite constraints: the
           censor processes a FILE and can spend 3× longer for better recall,
           while /voice-translator answers a live conversation turn and measured
           `base` at RTF ~1.1 on WASM — six seconds of speech costing six
           seconds of silence. Same engine, different budget. */
        var modelSize = cfg.whisperModel || pickWhisperModel();
        /* Word timestamps are the censor's whole point (it mutes spans) and
           dead weight for a translator (it needs a sentence). Asking for them
           is not free: the worker rejects the job outright when the model
           returns phrase-level only, so a caller that does not need words must
           be able to say so rather than lose the transcript it did get. */
        var wantWords = cfg.wordTimestamps !== false;
        var wordLevel = true;   // false ⇒ only phrase-level timestamps came back
        // 'webgpu' | 'wasm' | null — captured from the worker's preload_done /
        // backend messages. Exposed via backend() so the tool can (a) key its
        // persisted speed measurement per backend (a GPU→WASM fallback is ~10×
        // slower — one shared number would poison the time estimate) and
        // (b) skip the long-job warning on first-visit WebGPU devices.
        var activeBackend = null;

        function ensure() {
            if (initPromise) return initPromise;
            initPromise = new Promise(function (resolve, reject) {
                try { worker = new Worker(cfg.urls.whisperWorker); }
                catch (e) { reject(e); return; }
                var settled = false;
                worker.onerror = function (ev) { if (!settled) { settled = true; reject(new Error((ev && ev.message) || 'worker error')); } };
                worker.addEventListener('message', function onInit(ev) {
                    var m = ev.data || {};
                    if (m.type === 'preload_done') {
                        settled = true;
                        worker.removeEventListener('message', onInit);
                        wordLevel = m.wordLevel !== false;
                        if (m.backend) activeBackend = m.backend;
                        hooks.onModelReady(0);
                        resolve();
                    } else if (m.type === 'preload_failed') {
                        settled = true;
                        worker.removeEventListener('message', onInit);
                        reject(new Error(m.message || 'whisper preload failed'));
                    } else if (m.type === 'download_progress') {
                        // whisper posts an aggregate 0..100 across its 5-7 files;
                        // the gigaam path reports MB, so express pct as MB-of-100.
                        var pct = (m.data && m.data.progress) || 0;
                        hooks.onModelProgress(Math.round(pct), 100);
                    } else if (m.type === 'status' && m.status === 'loading_model') {
                        hooks.onModelPreparing();
                    }
                });
                worker.postMessage({ type: 'preload', modelSize: modelSize, wordTimestamps: wantWords });
            });
            initPromise.catch(function () {
                initPromise = null;
                try { if (worker) worker.terminate(); } catch (e) { /* noop */ }
                worker = null;
            });
            return initPromise;
        }

        function transcribe(pcmF32, job, opts) {
            return new Promise(function (resolve, reject) {
                var w = worker;
                var totalS = pcmF32.length / SR;
                var startedAt = Date.now();
                var handler = function (ev) {
                    var m = ev.data || {};
                    if (hooks.isStale(job)) { cleanup(); reject(abortedError()); return; }
                    if (m.type === 'transcribe_progress') {
                        // audioTime = how far through the audio the decoder is → the
                        // same (doneS,totalS) contract the gigaam path emits, so the
                        // predictive bar + liveness watchdog work unchanged.
                        var doneS = Math.min(m.audioTime || 0, totalS);
                        var elapsed = (Date.now() - startedAt) / 1000;
                        hooks.onProgress(doneS, totalS, elapsed > 0.5 ? doneS / elapsed : 0);
                    } else if (m.type === 'preprocess_progress') {
                        // mel/feature phase — real forward motion BEFORE any token
                        // (transformers.js preprocesses the whole file up front).
                        hooks.onPreprocess(m.done || 0, m.total || 0);
                    } else if (m.type === 'backend' || m.type === 'webgpu_fallback') {
                        // Track the ACTIVE backend across in-worker recoveries: a
                        // WebGPU→WASM fallback mid-job must re-key the persisted
                        // speed measurement, or the fast-GPU number would predict
                        // a 10×-slower WASM run.
                        if (m.type === 'backend' && m.backend) activeBackend = m.backend;
                        if (m.type === 'webgpu_fallback') activeBackend = 'wasm';
                        hooks.onAlive(m.type);
                    } else if (m.type === 'heartbeat' || m.type === 'partial_text' ||
                               m.type === 'status' ||
                               m.type === 'chunk_done' || m.type === 'download_progress' ||
                               m.type === 'model_downgraded') {
                        // Liveness, not progress: the worker is demonstrably alive
                        // (tokens streaming, model reloading for an in-worker
                        // recovery, …). The page-side watchdog was killing HEALTHY
                        // long-file runs because it only ever saw the per-chunk
                        // transcribe_progress — prod 2026-07-16, comment #852.
                        hooks.onAlive(m.type);
                    } else if (m.type === 'word_level_degraded') {
                        wordLevel = false;   // surfaced with the result; the tool decides
                    } else if (m.type === 'result') {
                        cleanup();
                        // Phrase-level timestamps would make every "region" a whole
                        // 29 s window — silencing half the file. Refuse honestly
                        // instead of shipping a censor that eats the conversation.
                        if (wantWords && (m.result.wordLevel === false || !wordLevel)) {
                            var wd = new Error('word timestamps unavailable');
                            wd.pcCode = 'no_word_ts';
                            reject(wd);
                            return;
                        }
                        // `text` is carried alongside the words because a caller
                        // that asked for wordTimestamps:false gets NO chunks —
                        // words would be an empty array and the transcript would
                        // silently come out blank. The censor keeps using words.
                        resolve({ words: chunksToWords(m.result.chunks), text: m.result.text || '', detectedLang: lang, stats: {} });
                    } else if (m.type === 'error') {
                        cleanup(); reject(new Error(m.message));
                    }
                };
                function cleanup() { w.removeEventListener('message', handler); }
                w.addEventListener('message', handler);
                var buf = pcmF32.buffer;
                // `language` is ALWAYS explicit — see the measurement note above.
                w.postMessage({
                    type: 'transcribe', audioBuffer: buf, modelSize: modelSize,
                    language: lang, wordTimestamps: wantWords
                }, [buf]);
            });
        }

        /* Whisper word chunks → the detector's word shape. A chunk whose end
           timestamp is null (last word of a window) inherits a 0.3 s tail so
           the censor region still covers it. */
        function chunksToWords(chunks) {
            var out = [];
            (chunks || []).forEach(function (c) {
                var text = (c.text || '').trim();
                if (!text) return;
                var ts = c.timestamp || [0, 0];
                var s = ts[0] || 0;
                var e = (ts[1] === null || ts[1] === undefined) ? s + 0.3 : ts[1];
                if (e <= s) e = s + 0.12;
                out.push({ text: text, start: s, end: e, conf: 1 });
            });
            return out;
        }

        var dead = false;
        return {
            engine: 'whisper',
            lang: lang,
            modelSize: modelSize,
            backend: function () { return activeBackend; },
            init: ensure,
            transcribe: transcribe,
            // transformers.js exposes no mid-generation abort: the only way to
            // stop a running transcribe is to kill the worker. Otherwise a
            // cancelled job keeps a CPU core pinned for the rest of the session.
            // The next run re-creates it; the model bytes come back from the
            // HTTP cache, so the cost is a WASM recompile (~1 s), not a download.
            abort: function () {
                dead = true;
                try { if (worker) worker.terminate(); } catch (e) { /* noop */ }
                worker = null; initPromise = null;
            },
            isDead: function () { return dead; },
            terminate: function () {
                dead = true;
                try { if (worker) worker.terminate(); } catch (e) { /* noop */ }
                worker = null; initPromise = null;
            }
        };
    }

    /* MEASURED (2026-07-09, whisper base via transformers.js): omitting the
       `language` option does NOT auto-detect — the decoder is forced to English
       and TRANSLATES. A Portuguese clip ("Que porra é essa que você fez com o
       meu carro?") came back as "What is this that you made with my car?".
       Word timestamps still line up, but the transcript is in the wrong
       language, so no lexicon can match and the tool would silently censor
       nothing. Therefore the language is ALWAYS explicit; `guessLangFromText`
       is used afterwards to offer the user a correction, never to drive ASR. */

    /* hooks: {onModelProgress, onModelPreparing, onModelReady, onProgress,
               onPreprocess, onAlive, onPartialWords, isStale} — all optional,
               defaulted to no-ops. */
    function create(lang, cfg, hooks) {
        var h = hooks || {};
        var safe = {
            onModelProgress: h.onModelProgress || function () {},
            onModelPreparing: h.onModelPreparing || function () {},
            onModelReady: h.onModelReady || function () {},
            onProgress: h.onProgress || function () {},
            onPreprocess: h.onPreprocess || function () {},
            onAlive: h.onAlive || function () {},
            onPartialWords: h.onPartialWords || function () {},
            isStale: h.isStale || function () { return false; }
        };
        if (lang === 'ru') return createGigaam(cfg, safe);
        if (SENSEVOICE_LANGS[lang] && cfg && cfg.urls && cfg.urls.sensevoice) return createCtc(ctcSenseVoice(lang), cfg, safe);
        return createWhisper(cfg, safe, lang);
    }

    /* The fine-tuned export this site ships is Chinese-only: it tags every input <|yue|> and
       renders Japanese / Korean as Han gibberish (measured — see the worker's header). */
    var SENSEVOICE_LANGS = { zh: 1, yue: 1 };

    var api = { create: create, pickWhisperModel: pickWhisperModel, guessLangFromText: guessLangFromText, SENSEVOICE_LANGS: SENSEVOICE_LANGS };
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    else root.AsrAdapter = api;
})(typeof self !== 'undefined' ? self : this);
